import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { fetchQueueCallRecordsWithBPart } from 'flow/actions/pbx-actions';
import { fetchCallRecordsForExtension } from 'flow/actions/extension-actions';
import CallRecordSkeleton, { CallRecordItemSkeleton } from 'flow/components/loaders/CallRecordSkeleton';
import { callHistoryRowKey, callRecordKeyDates, rowIsFocusable } from 'flow/components/call-history-v2/call-history-rows';
import DateGroupedList from 'flow/components/call-history-v2/DateGroupedList';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { olderQueueCallRecordsWithBPartAreAvailableInApi } from 'flow/selectors/queue-selectors';
import { makeSelectOwnCallRecordKeysByPhoneNumberNotActiveDateGroups, makeSelectQueueCallRecordKeysWithBPartNotActiveDateGroups, selectCallRecordFromKey, selectOwnCallRecordsNextBeforeForNumber } from 'flow/selectors/call-records-selectors';
import { CALL_RECORD_INCLUDES_RELATED_EXTENSION } from 'flow/utils/call-record-fetch-includes';
import RelatedCallRecordItem from './RelatedCallRecordItem';

/** The tab panel's scroller, when the page gives it no id of its own. */
import { jsx as _jsx } from "react/jsx-runtime";
var DEFAULT_SCROLLABLE_ID = 'main-scrollable-div';

// --- Queue-scoped related calls ---

export var QueueRelatedTabContent = function QueueRelatedTabContent(_ref) {
  var callRecordKey = _ref.callRecordKey,
    onNavigateToCallRecord = _ref.onNavigateToCallRecord,
    scrollableId = _ref.scrollableId;
  var callRecord = useAppSelector(function (state) {
    return selectCallRecordFromKey(state, callRecordKey);
  });
  var queueKey = callRecord === null || callRecord === void 0 ? void 0 : callRecord.getIn(['queue', 'key']);
  var callerNumber = callRecord === null || callRecord === void 0 ? void 0 : callRecord.getIn(['number', 'number']);
  if (!callerNumber || !queueKey) return null;
  return /*#__PURE__*/_jsx(QueueRelatedContent, {
    queueKey: queueKey,
    callerNumber: callerNumber,
    excludeCallRecordKey: callRecordKey,
    onNavigateToCallRecord: onNavigateToCallRecord,
    scrollableId: scrollableId
  });
};
var QueueRelatedContent = function QueueRelatedContent(_ref2) {
  var queueKey = _ref2.queueKey,
    callerNumber = _ref2.callerNumber,
    excludeCallRecordKey = _ref2.excludeCallRecordKey,
    onNavigateToCallRecord = _ref2.onNavigateToCallRecord,
    scrollableId = _ref2.scrollableId;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoadingMore = _useState2[0],
    setIsLoadingMore = _useState2[1];
  var handleItemClick = useCallback(function (callRecordKey) {
    if (onNavigateToCallRecord) {
      onNavigateToCallRecord(callRecordKey);
    } else {
      openModal('callRecordDetailV2', {
        callRecordKey: callRecordKey,
        queueKey: queueKey,
        pageType: 'pbx',
        segmentContext: 'PBX call detail'
      });
    }
  }, [onNavigateToCallRecord, openModal, queueKey]);
  useEffect(function () {
    dispatch(fetchQueueCallRecordsWithBPart(queueKey, callerNumber));
  }, [dispatch, queueKey, callerNumber]);
  var initialLoading = useAppSelector(function (state) {
    return state.callRecordsData.getIn(['sortingData', 'queues', queueKey, 'byPhoneNumber', callerNumber]) === undefined;
  });
  var selectGroupedKeys = useMemo(makeSelectQueueCallRecordKeysWithBPartNotActiveDateGroups, []);
  var callRecordKeys = useAppSelector(function (state) {
    return selectGroupedKeys(state, queueKey, callerNumber);
  });
  var hasMore = useAppSelector(function (state) {
    return olderQueueCallRecordsWithBPartAreAvailableInApi(state, queueKey, callerNumber);
  });
  var recordCount = (callRecordKeys === null || callRecordKeys === void 0 ? void 0 : callRecordKeys.reduce(function (sum, group) {
    return sum + group.size;
  }, 0)) || 0;
  return /*#__PURE__*/_jsx(InfiniteScroll, {
    className: "overflow-visible!",
    next: function next() {
      setIsLoadingMore(true);
      dispatch(fetchQueueCallRecordsWithBPart(queueKey, callerNumber, true))["finally"](function () {
        return setIsLoadingMore(false);
      });
    },
    dataLength: recordCount,
    hasMore: !!hasMore,
    loader: isLoadingMore ? /*#__PURE__*/_jsx(CallRecordItemSkeleton, {}) : null,
    scrollableTarget: scrollableId !== null && scrollableId !== void 0 ? scrollableId : DEFAULT_SCROLLABLE_ID,
    endMessage: !initialLoading && recordCount > 0 ? /*#__PURE__*/_jsx("div", {
      className: "flex justify-center py-9 text-sm text-gray-500",
      children: i18next.t('call.history.title.last')
    }) : null,
    children: initialLoading ? /*#__PURE__*/_jsx(CallRecordSkeleton, {}) : /*#__PURE__*/_jsx(RelatedCallRecordList, {
      callRecordKeysByDate: callRecordKeys,
      excludeCallRecordKey: excludeCallRecordKey,
      queueKey: queueKey,
      onItemClick: handleItemClick,
      pageType: "pbx"
    })
  });
};

// --- Personal call history related calls ---

export var PersonalRelatedTabContent = function PersonalRelatedTabContent(_ref3) {
  var callRecordKey = _ref3.callRecordKey,
    scrollableId = _ref3.scrollableId;
  var callRecord = useAppSelector(function (state) {
    return selectCallRecordFromKey(state, callRecordKey);
  });
  var callerNumber = callRecord === null || callRecord === void 0 ? void 0 : callRecord.getIn(['number', 'number']);
  if (!callerNumber) return null;
  return /*#__PURE__*/_jsx(PersonalRelatedContent, {
    callerNumber: callerNumber,
    excludeCallRecordKey: callRecordKey,
    scrollableId: scrollableId
  });
};
var PersonalRelatedContent = function PersonalRelatedContent(_ref4) {
  var callerNumber = _ref4.callerNumber,
    excludeCallRecordKey = _ref4.excludeCallRecordKey,
    scrollableId = _ref4.scrollableId;
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next;
  var dispatch = useAppDispatch();
  var _useModal2 = useModal(),
    openModal = _useModal2.openModal;
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoadingMore = _useState4[0],
    setIsLoadingMore = _useState4[1];
  var handleItemClick = useCallback(function (callRecordKey) {
    openModal('callRecordDetailV2', {
      callRecordKey: callRecordKey,
      pageType: 'personal',
      segmentContext: 'Personal call detail'
    });
  }, [openModal]);
  useEffect(function () {
    dispatch(fetchCallRecordsForExtension(ownExtensionKey, CALL_RECORD_INCLUDES_RELATED_EXTENSION, null, null, callerNumber));
  }, [dispatch, ownExtensionKey, callerNumber]);
  var selectGroupedKeys = useMemo(makeSelectOwnCallRecordKeysByPhoneNumberNotActiveDateGroups, []);
  var callRecordKeys = useAppSelector(function (state) {
    return selectGroupedKeys(state, callerNumber);
  });
  var initialLoading = callRecordKeys === undefined;
  var nextBefore = useAppSelector(function (state) {
    return selectOwnCallRecordsNextBeforeForNumber(state, callerNumber);
  });
  var hasMore = nextBefore !== null;
  var recordCount = (callRecordKeys === null || callRecordKeys === void 0 ? void 0 : callRecordKeys.reduce(function (sum, group) {
    return sum + group.size;
  }, 0)) || 0;
  return /*#__PURE__*/_jsx(InfiniteScroll, {
    className: "overflow-visible!",
    next: function next() {
      setIsLoadingMore(true);
      dispatch(fetchCallRecordsForExtension(ownExtensionKey, CALL_RECORD_INCLUDES_RELATED_EXTENSION, null, nextBefore, callerNumber))["finally"](function () {
        return setIsLoadingMore(false);
      });
    },
    dataLength: recordCount,
    hasMore: hasMore,
    loader: isLoadingMore ? /*#__PURE__*/_jsx(CallRecordItemSkeleton, {}) : null,
    scrollableTarget: scrollableId !== null && scrollableId !== void 0 ? scrollableId : DEFAULT_SCROLLABLE_ID,
    endMessage: !initialLoading && recordCount > 0 ? /*#__PURE__*/_jsx("div", {
      className: "flex justify-center py-9 text-sm text-gray-500",
      children: i18next.t('call.history.title.last')
    }) : null,
    children: initialLoading ? /*#__PURE__*/_jsx(CallRecordSkeleton, {}) : /*#__PURE__*/_jsx(RelatedCallRecordList, {
      callRecordKeysByDate: callRecordKeys,
      excludeCallRecordKey: excludeCallRecordKey,
      onItemClick: handleItemClick,
      pageType: "personal"
    })
  });
};

// --- Shared list rendering ---

/**
 * The other calls from the same number by date, as one Tab stop. The open call
 * is not among them, so Tab enters at the first call until a call has had
 * focus. A call's name is a button that opens the call.
 */
export var RelatedCallRecordList = /*#__PURE__*/React.memo(function RelatedCallRecordList(_ref5) {
  var callRecordKeysByDate = _ref5.callRecordKeysByDate,
    excludeCallRecordKey = _ref5.excludeCallRecordKey,
    queueKey = _ref5.queueKey,
    onItemClick = _ref5.onItemClick,
    pageType = _ref5.pageType;
  var _useAppContext3 = useAppContext(),
    i18next = _useAppContext3.i18next;
  var dates = useMemo(function () {
    return callRecordKeyDates(callRecordKeysByDate, excludeCallRecordKey);
  }, [callRecordKeysByDate, excludeCallRecordKey]);
  var renderRow = function renderRow(row, rowProps) {
    return /*#__PURE__*/_jsx(RelatedCallRecordItem, _objectSpread({
      callRecordKey: row.callRecordKey,
      queueKey: queueKey,
      pageType: pageType,
      onOpen: onItemClick
    }, rowProps));
  };
  if (dates.length === 0) {
    return /*#__PURE__*/_jsx("div", {
      className: "flex justify-center py-9 text-sm text-gray-500",
      children: i18next.t('call.history.related.empty')
    });
  }
  return /*#__PURE__*/_jsx(DateGroupedList, {
    dates: dates,
    rowKey: callHistoryRowKey,
    rowIsFocusable: rowIsFocusable,
    renderRow: renderRow
  });
});